import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Button,
  Col,
  Divider,
  Row,
  Space,
  Table,
  Typography,
  Card,
  Statistic,
  Input,
  Select,
} from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import CreateReparation from "./components/CreateReparation";
import {
  useReparationServiceGetReparationCases,
  useReparationServiceGetReparationCasesKey,
} from "api/queries";
import { ColumnType } from "antd/es/table";
import { IReparationCaseListItem } from "api/requests";
import CaseStatusTag from "./components/CaseStatusTag";
import { useQueryClient } from "@tanstack/react-query";
import { TableActions } from "components/common";
import { useNavigate } from "react-router-dom";
import { useAbility } from "@casl/react";
import { AbilityContext } from "utils/abilit";
import { ExtraPermissionType, PermissionType, SubjectKey } from "utils/enums";
import PrintReparationDrawer from "./components/PrintReparationDrawer";
import useFilters, { FilterTemplate, FilterType } from "hooks/UseFilter";
import useAuth from "hooks/UseAuth";
import QueryString from "qs";

export default function ReparationList() {
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [sortQuery, setSortQuery] = useState("");
  const [search, setSearch] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [dateRangePreset, setDateRangePreset] = useState("custom");
  const [presetDateRange, setPresetDateRange] = useState<{
    startDate: string | null;
    endDate: string | null;
  }>({ startDate: null, endDate: null });
  const [createReparationDrawerOpen, setCreateReparationDrawerOpen] =
    useState(false);
  const [printDrawerOpen, setPrintDrawerOpen] = useState(false);
  const [selectedCase, setSelectedCase] =
    useState<IReparationCaseListItem | null>(null);

  const queryClient = useQueryClient();
  const nav = useNavigate();
  const authContext = useAuth();

  const ability = useAbility(AbilityContext);

  const filtersDef: any[] = [
    {
      key: "reparationCaseStatus",
      label: "Case Status",
      type: FilterType.MultiSelect,
      options: [
        { label: "Pending", value: "PENDING" },
        { label: "Checking", value: "CHECKING" },
        { label: "Revised", value: "REVISED" },
        { label: "Priced", value: "PRICED" },
        { label: "Contacting Customer", value: "CONTACTING_CUSTOMER" },
        { label: "Customer Confirmed", value: "CUSTOMER_CONFIRMED" },
        { label: "Repairing", value: "REPAIRING" },
        { label: "Completed", value: "COMPLETED" },
        { label: "Paid", value: "PAID" },
        { label: "Cancelled", value: "CANCELLED" },
        { label: "Received", value: "RECEIVED" },
      ],
    },
    {
      key: "createdDate",
      label: "Creation Date",
      type: FilterType.DateRange,
    },
    {
      key: "location",
      label: "Location",
      type: FilterType.template,
      template: FilterTemplate.LocationSelect,
    },
    ...(ability.can(ExtraPermissionType.MANAGE, SubjectKey.REPARATIONS)
      ? [
          {
            key: "createdBy",
            label: "Employee",
            type: FilterType.template,
            template: FilterTemplate.UserSelect,
          },
        ]
      : []),
  ];

  const { filtersQuery, FilterButton, Filters } = useFilters(filtersDef);

  // Add hidden createdBy filter if user doesn't have MANAGE permission
  const finalFiltersQuery = useMemo(() => {
    const baseQuery = {
      ...filtersQuery,
      ...(ability.can(ExtraPermissionType.MANAGE, SubjectKey.REPARATIONS)
        ? {}
        : currentUserId
        ? { createdBy: currentUserId }
        : {}),
    };

    // Add preset date range if not custom
    if (presetDateRange.startDate && presetDateRange.endDate) {
      baseQuery.createdDate = {
        $gte: presetDateRange.startDate,
        $lte: presetDateRange.endDate,
      };
    }

    return baseQuery;
  }, [filtersQuery, ability, currentUserId, presetDateRange]);

  // Handle date range preset changes
  const handleDateRangePresetChange = (value: string) => {
    setDateRangePreset(value);

    const now = dayjs();
    let startDate: string | null = null;
    let endDate: string | null = null;

    switch (value) {
      case "today":
        startDate = now.startOf("day").format("YYYY-MM-DDTHH:mm:ss");
        endDate = now.endOf("day").format("YYYY-MM-DDTHH:mm:ss");
        break;
      case "thisWeek":
        startDate = now.startOf("week").format("YYYY-MM-DDTHH:mm:ss");
        endDate = now.endOf("week").format("YYYY-MM-DDTHH:mm:ss");
        break;
      case "thisMonth":
        startDate = now.startOf("month").format("YYYY-MM-DDTHH:mm:ss");
        endDate = now.endOf("month").format("YYYY-MM-DDTHH:mm:ss");
        break;
      case "thisYear":
        startDate = now.startOf("year").format("YYYY-MM-DDTHH:mm:ss");
        endDate = now.endOf("year").format("YYYY-MM-DDTHH:mm:ss");
        break;
      case "custom":
      default:
        startDate = null;
        endDate = null;
        break;
    }

    setPresetDateRange({ startDate, endDate });
  };

  const dateRangeOptions = [
    { label: "Custom", value: "custom" },
    { label: "Today", value: "today" },
    { label: "This Week", value: "thisWeek" },
    { label: "This Month", value: "thisMonth" },
    { label: "This Year", value: "thisYear" },
  ];

  const { isLoading, data } = useReparationServiceGetReparationCases(
    {
      search,
      limit,
      offset: (page - 1) * limit,
      sort: sortQuery,
      status: "",
      filters: QueryString.stringify(finalFiltersQuery),
    },
    [useReparationServiceGetReparationCasesKey]
  );

  const columns: ColumnType<IReparationCaseListItem>[] = [
    {
      key: "reparationCaseNumber",
      dataIndex: "reparationCaseNumber",
      title: "Case ID",
      sorter: true,
    },
    {
      key: "customerName",
      dataIndex: "firstName",
      title: "Customer",
      render: (_value: string, r) => r.firstName + " " + r.lastName,
      sorter: true,
    },
    {
      key: "primaryPhone",
      dataIndex: "primaryPhone",
      title: "Phone",
      sorter: true,
    },
    {
      key: "model",
      dataIndex: "model",
      title: "Device",
      render: (_value: string, r) => r.brand?.brandName + " " + r.model,
      sorter: false,
    },
    {
      key: "reportedCaseIssue",
      dataIndex: "reportedCaseIssue",
      title: "Issue",
      sorter: true,
    },
    {
      key: "reparationCaseStatus",
      dataIndex: "reparationCaseStatus",
      title: "Status",
      render: (v) => <CaseStatusTag status={v} />,
      sorter: true,
    },
    {
      key: "priced",
      dataIndex: "totalPrice",
      title: "Priced",
      render: (_value: number, record: any) => {
        const constTotalPrice = record?.reparationItems?.reduce(
          (acc: number, item: any) => acc + (item?.totalPrice || 0),
          0
        );
        return !constTotalPrice || constTotalPrice < 0.01 ? (
          <span style={{ color: "red", fontWeight: 600 }}>
            <span style={{ marginRight: 4 }}>✗</span> Not Priced
          </span>
        ) : (
          <span style={{ color: "green", fontWeight: 600 }}>
            <span style={{ marginRight: 4 }}>✔</span> {constTotalPrice}
          </span>
        );
      },
      sorter: false,
    },
    {
      key: "paymentMethod",
      dataIndex: "paymentMethod",
      title: "Payment Method",
      render: (method: any) => method?.paymentMethodName || "N/A",
      sorter: true,
    },
    {
      key: "createdDate",
      dataIndex: "createdDate",
      title: "Created Date",
      render: (value: string) => dayjs(value).format("DD MMM YYYY"),
      sorter: true,
    },
    {
      key: "actions",
      title: "Actions",
      render: (value: any, record: any) => (
        <TableActions
          onView={() => handleView(record.idReparationCase)}
          onPrint={
            ability.can(PermissionType.READ, SubjectKey.REPARATIONS)
              ? () => handlePrint(record)
              : undefined
          }
        />
      ),
    },
  ];

  const handleView = (reparationCaseId: string) => {
    nav(`/sales/reparations/${reparationCaseId}`);
  };

  const handlePrint = (record: IReparationCaseListItem) => {
    setSelectedCase(record);
    setPrintDrawerOpen(true);
  };

  const handleClosePrintDrawer = () => {
    setPrintDrawerOpen(false);
    setSelectedCase(null);
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    if (pagination && pagination.current && pagination.pageSize) {
      setPage(pagination.current || 1);
      setLimit(pagination.pageSize || 10);
    }

    if (sorter) {
      if (sorter.order) {
        const s: any = {};
        s[sorter.field] = sorter.order === "ascend" ? "asc" : "desc";
        setSortQuery(s);
      } else {
        setSortQuery("");
      }
    }
  };

  useEffect(() => {
    queryClient.refetchQueries({
      queryKey: [useReparationServiceGetReparationCasesKey],
    });
  }, [sortQuery, limit, page, search, finalFiltersQuery, queryClient]);

  useEffect(() => {
    if (authContext?.userDetails && authContext?.userDetails !== "") {
      const userDetails: any = JSON.parse(authContext.userDetails);
      setCurrentUserId(userDetails.idUser);
    }
  }, [authContext.userDetails]);

  const handleCreateReparation = () => {
    setCreateReparationDrawerOpen(true);
  };

  const handleCloseCreateReparation = () => {
    setCreateReparationDrawerOpen(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
    >
      <Row>
        <Col xs={24}>
          <Row gutter={[16, 16]} justify="center">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card>
                <Statistic
                  title="New Cases Today"
                  value={data?.totalNewCasesToday || 0}
                  loading={isLoading}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card>
                <Statistic
                  title="Pending Cases"
                  value={data?.totalPendingCasesToday || 0}
                  loading={isLoading}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card>
                <Statistic
                  title="Completed Today"
                  value={data?.totalCompletedCasesToday || 0}
                  loading={isLoading}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card>
                <Statistic
                  title="Total Cases"
                  value={data?.total || 0}
                  loading={isLoading}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card>
                <Statistic
                  title="Total Amount"
                  value={Number(data?.totalAmount || 0).toLocaleString(
                    undefined,
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                  )}
                  loading={isLoading}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card>
                <Statistic
                  title="Total Paid"
                  value={Number(data?.totalPaid || 0).toLocaleString(
                    undefined,
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                  )}
                  loading={isLoading}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card>
                <Statistic
                  title="Total Unpaid"
                  value={Number(data?.totalUnpaid || 0).toLocaleString(
                    undefined,
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                  )}
                  loading={isLoading}
                />
              </Card>
            </Col>
            
            {data?.statusKpis &&
              data?.statusKpis.length > 0 &&
              data?.statusKpis.map((status: any) => {
                const statusLabels: { [key: string]: string } = {
                  PENDING: "Pending",
                  CHECKING: "Checking",
                  REVISED: "Revised",
                  PRICED: "Priced",
                  CONTACTING_CUSTOMER: "Contacting Customer",
                  CUSTOMER_CONFIRMED: "Customer Confirmed",
                  REPAIRING: "Repairing",
                  COMPLETED: "Completed",
                  PAID: "Paid",
                  CANCELLED: "Cancelled",
                };
                
                return (
                  <Col
                    xs={24} sm={12} md={8} lg={6}
                    key={status.statusName}
                  >
                    <Card>
                      <Statistic
                        title={statusLabels[status.statusName] || status.statusName}
                        value={status.total || 0}
                        loading={isLoading}
                      />
                    </Card>
                  </Col>
                );
              })}

            {data?.paymentMethods &&
              data?.paymentMethods.length > 0 &&
              data?.paymentMethods.map((method: any) => {
                return (
                  <Col
                    xs={24} sm={12} md={8} lg={6}
                    key={method.name}
                  >
                    <Card>
                      <Statistic
                        title={`Total ${method.name}`}
                        value={Number(method.amount || 0).toLocaleString(
                          undefined,
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                        )}
                        loading={isLoading}
                      />
                    </Card>
                  </Col>
                );
              })}
          </Row>
        </Col>
        <Col xs={12}>
          <Typography.Title>Reparations</Typography.Title>
        </Col>
        <Col
          xs={12}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <Space>
            {FilterButton}
            <Button
              icon={<PlusCircleOutlined />}
              type="primary"
              onClick={handleCreateReparation}
              disabled={
                !ability.can(PermissionType.CREATE, SubjectKey.REPARATIONS)
              }
            >
              Add New
            </Button>
          </Space>
        </Col>

        <Col xs={24}>{Filters}</Col>

        <Col xs={24}>
          <Divider style={{ opacity: 0.4 }} />
        </Col>

        <Col xs={24} style={{ marginBottom: 15 }}>
          <Space.Compact style={{ width: "100%" }}>
            <Input.Search
              placeholder="Search by reparation case number"
              style={{ flex: 1 }}
              onSearch={setSearch}
              loading={isLoading}
              allowClear={true}
            />
            <Select
              value={dateRangePreset}
              onChange={handleDateRangePresetChange}
              options={dateRangeOptions}
              style={{ width: 150 }}
              placeholder="Date Range"
            />
          </Space.Compact>
        </Col>

        <Col xs={24}>
          <Table
            scroll={{ x: "max-content" }}
            columns={columns}
            dataSource={data?.items}
            loading={isLoading}
            pagination={{
              total: data?.total || 0,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ["10", "20", "30", "40", "50"],
            }}
            onChange={handleTableChange}
            rowKey={(item) => item.idReparationCase}
          />
        </Col>

        <CreateReparation
          isOpen={createReparationDrawerOpen}
          onClose={handleCloseCreateReparation}
        />
        <PrintReparationDrawer
          isOpen={printDrawerOpen}
          onClose={handleClosePrintDrawer}
          reparationCase={selectedCase}
        />
      </Row>
    </motion.div>
  );
}
